import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCreateListingDialog } from "@/contexts/create-listing-dialog-provider";
import { useListings } from "@/contexts/listings-provider";
import { useAdminMode } from "@/contexts/admin-mode-provider";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { CaretSortIcon, CheckIcon, PlusCircledIcon } from "@radix-ui/react-icons";
import { EmployeeDTO, ListingDTO } from "@shaarp/common";
import { useEffect, useState } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { toast, useToastError } from "../ui/use-toast";

export default function EmployeeListingsSelector({
  employee,
}: {
  employee: EmployeeDTO;
}) {
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();
  const { isAdminMode } = useAdminMode();

  const { userListings, shaarpListings, apiFetchListings } = useListings();
  const { openCreateListingDialog } = useCreateListingDialog();

  const [open, setOpen] = useState<boolean>(false);

  const [listingsIds, setListingsIds] = useState<Set<string>>(
    () => new Set(employee.listingsIds ?? [])
  );

  useEffect(() => {
    setListingsIds(new Set(employee.listingsIds ?? []));
  }, [employee.listingsIds]);

  useEffect(() => {
    const init = async () => {
      await apiFetchListings();
    };
    init();
  }, []);

  const employeeUserListings = userListings.filter((l) => l.listingType === "employees");
  const employeeShaarpListings = shaarpListings.filter((l) => l.listingType === "employees");

  const availableListings = [...employeeUserListings, ...employeeShaarpListings];

  const selectListing = async (listing: ListingDTO) => {
    if (listingsIds.has(listing.id)) {
      await removeFromListing(listing);
    } else {
      await addToListing(listing);
    }
    await apiFetchListings({ force: true });
  };

  const addToListing = async (listing: ListingDTO) => {
    setListingsIds((prev) => {
      const next = new Set(prev);
      next.add(listing.id);
      return next;
    });
    try {
      await api.post(`/listing/${listing.id}/employees`, {
        employeesIds: [employee.id],
      });
      toast({
        variant: "default",
        title: t("components.dataTableCompanyEmployees.myLists.addedToList", {
          listingTitle: listing.title,
        }),
        description: t(
          "components.dataTableCompanyEmployees.myLists.addedToListDescription",
          {
            employeeName: [employee.firstName, employee.lastName]
              .filter(Boolean)
              .join(" "),
          }
        ),
      });
    } catch (error) {
      setListingsIds((prev) => {
        const next = new Set(prev);
        next.delete(listing.id);
        return next;
      });
      toastError();
    }
  };

  const removeFromListing = async (listing: ListingDTO) => {
    setListingsIds((prev) => {
      const next = new Set(prev);
      next.delete(listing.id);
      return next;
    });
    try {
      await api.delete(`/listing/${listing.id}/employees`, {
        data: { employeesIds: [employee.id] },
      });
      toast({
        variant: "default",
        title: t(
          "components.dataTableCompanyEmployees.myLists.removedFromList",
          {
            listingTitle: listing.title,
          }
        ),
        description: t(
          "components.dataTableCompanyEmployees.myLists.removedFromListDescription",
          {
            employeeName: [employee.firstName, employee.lastName]
              .filter(Boolean)
              .join(" "),
          }
        ),
      });
    } catch (error) {
      setListingsIds((prev) => {
        const next = new Set(prev);
        next.add(listing.id);
        return next;
      });
      toastError();
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            size="sm"
            className={`gap-2 ${
              listingsIds.size > 0 ? "text-primary" : "text-gray-400"
            }`}
          >
            <div>
              {t("components.dataTableCompanyEmployees.myLists.inLists", {
                count: listingsIds.size,
              })}
            </div>
            <CaretSortIcon className="w-4 h-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0" side="bottom" align="center">
          <Command>
            {availableListings.length > 0 ? (
              <>
                <CommandInput placeholder={t("common.search")} />
                <CommandList>
                  <CommandEmpty>{t("common.noResults")}</CommandEmpty>
                  {employeeUserListings.length > 0 && (
                    <CommandGroup
                      heading={t("company.listings.personalLists")}
                    >
                      {employeeUserListings.map((listing) => (
                        <CommandItem
                          key={listing.id}
                          value={listing.title}
                          onSelect={() => selectListing(listing)}
                          className="justify-between"
                        >
                          <span>
                            <CheckIcon
                              className={cn(
                                "inline-block h-4 w-4 mr-2 text-primary opacity-0",
                                listingsIds.has(listing.id) && "opacity-100"
                              )}
                            />
                            {listing.title}
                          </span>
                          {listing.employeesCount === 0 ? (
                            <Badge variant="outline" className="ml-auto">
                              {t("listing.page.list.badges.empty")}
                            </Badge>
                          ) : (
                            <Badge variant="default" className="ml-auto">
                              {listing.employeesCount}
                            </Badge>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {employeeShaarpListings.length > 0 && (
                    <CommandGroup heading={t("company.listings.shaarpLists")}>
                      {employeeShaarpListings.map((listing) => (
                        <CommandItem
                          key={listing.id}
                          value={listing.title}
                          disabled={!isAdminMode}
                          onSelect={() => selectListing(listing)}
                          className="justify-between"
                        >
                          <span>
                            <CheckIcon
                              className={cn(
                                "inline-block h-4 w-4 mr-2 text-primary opacity-0",
                                listingsIds.has(listing.id) && "opacity-100"
                              )}
                            />
                            {listing.title}
                          </span>
                          {listing.employeesCount === 0 ? (
                            <Badge variant="outline" className="ml-auto">
                              {t("listing.page.list.badges.empty")}
                            </Badge>
                          ) : (
                            <Badge variant="default" className="ml-auto">
                              {listing.employeesCount}
                            </Badge>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </>
            ) : (
              <CommandGroup heading={t("company.listings.personalLists")}>
                <CommandItem disabled={true}>
                  {t("company.listings.noLists")}
                </CommandItem>
              </CommandGroup>
            )}
            <CommandSeparator />
            <CommandList>
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    setOpen(false);
                    openCreateListingDialog({
                      defaultListingType: "employees",
                      onCreated: (newListing) => selectListing(newListing),
                    });
                  }}
                >
                  <PlusCircledIcon className="mr-2 w-5 h-5" />
                  {t("company.listings.newList")}
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
}
